// The daily reminder and the home screen: an ask under today's results, and their rows in Settings,
// with a test notification to check this device.
import { useState } from 'react'
import { Link } from 'react-router'
import type { ReminderTest } from '@wbwl/contract'
import { ApiFailure } from '../lib/api.ts'
import { storage } from '../lib/game.ts'
import { install, useInstall } from '../lib/install.ts'
import {
  arrival, formatHour, plannedHour, sendTestReminder, setReminderHour, turnOffReminder, turnOnReminder, useReminder, type ReminderState,
} from '../lib/reminders.ts'
import { Icon } from './Icon.tsx'
import { useToast } from './shell.tsx'

const DISMISSED_KEY = 'lof:keep-dismissed'
/** "Not now" hides the card for two weeks. */
const QUIET_MS = 14 * 86_400_000

const dismissedLately = () => Date.now() - Number(storage.get(DISMISSED_KEY) ?? 0) < QUIET_MS

/** The reminder switch, with its toasts; `busy` while a request is out. */
function useReminderSwitch() {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  /** `off` is the toast when the change leaves it off: turning it off, or a permission prompt that wasn't allowed. */
  async function run(change: () => Promise<ReminderState>, failed: string, off = 'Notifications weren’t allowed, so the reminder is off.') {
    setBusy(true)
    try {
      const s = await change()
      if (s.kind === 'on') toast(<><Icon name="check" />Reminder on · {formatHour(s.hour)} each day</>)
      else if (s.kind === 'blocked') toast('Notifications are blocked for this site. Allow them in your browser’s settings.', 3200)
      else if (s.kind === 'off') toast(off)
      return s
    } catch {
      toast(failed)
      return null
    } finally {
      setBusy(false)
    }
  }
  return {
    busy,
    on: (hour?: number) => run(() => turnOnReminder(hour), 'Couldn’t turn on the reminder. Try again.'),
    hour: (hour: number) => run(() => setReminderHour(hour), 'Couldn’t change the time. Try again.'),
    off: () => run(turnOffReminder, 'Couldn’t turn off the reminder. Try again.', 'Reminder off'),
  }
}

async function addToHomeScreen() {
  await install().catch(() => false)
}

/** Add to home screen, where the browser offers it: under a guest's Sign in on Results, in place of the reminder's ask. */
export function InstallButton() {
  const way = useInstall()
  if (way !== 'prompt') return null
  return <div className="keep-actions"><button type="button" className="btn ghost sm" onClick={addToHomeScreen}>Add to home screen</button></div>
}

/** Under the countdown on today's results: offer the daily reminder, or adding the site to the home screen. */
export function KeepAsk() {
  const reminder = useReminder()
  const way = useInstall()
  const flip = useReminderSwitch()
  const [hidden, setHidden] = useState(dismissedLately)
  const offerReminder = reminder.kind === 'off'
  const iosFirst = reminder.kind === 'needs-install'
  const offerInstall = way === 'prompt'
  if (hidden || (!offerReminder && !iosFirst && !offerInstall)) return null

  function dismiss() {
    storage.set(DISMISSED_KEY, String(Date.now()))
    setHidden(true)
  }

  return (
    <div className="next-ask">
      <div className="ask-say">
        <Icon name={offerReminder || iosFirst ? 'clock' : 'flame'} size={20} />
        {offerReminder ? (
          <div>
            <b>Want a nudge tomorrow?</b>
            <span className="muted small">One notification at {formatHour(plannedHour())}, only if you haven’t played yet. Change the time in <Link to="/you/settings">Settings</Link>.</span>
          </div>
        ) : iosFirst ? (
          <div>
            <b>Get a daily reminder</b>
            <span className="muted small">Add Letters on Fire to your Home Screen first: in Safari, tap Share, then Add to Home Screen. Open it from there and turn the reminder on.</span>
          </div>
        ) : (
          <div>
            <b>Keep it one tap away</b>
            <span className="muted small">Add Letters on Fire to your home screen and open each edition like an app.</span>
          </div>
        )}
      </div>
      <div className="keep-actions">
        {offerReminder && <button type="button" className="btn ink sm" disabled={flip.busy} onClick={() => void flip.on()}>Remind me</button>}
        {offerInstall && <button type="button" className={`btn ${offerReminder ? 'ghost' : 'ink'} sm`} onClick={addToHomeScreen}>Add to home screen</button>}
        <button type="button" className="btn plain sm" onClick={dismiss}>{iosFirst ? 'Got it' : 'Not now'}</button>
      </div>
    </div>
  )
}

const HOURS = Array.from({ length: 24 }, (_, h) => h)

/** Settings: the reminder's switch and time, and adding to the home screen. Nothing when neither applies here. */
export function ReminderSettings() {
  const reminder = useReminder()
  const way = useInstall()
  const flip = useReminderSwitch()
  if (reminder.kind === 'none' && !way) return null
  return (
    <>
      <div className="eyebrow set-group">Reminders</div>
      <div className="card list">
        {(reminder.kind === 'on' || reminder.kind === 'off') && (
          <div className="set">
            <div className="lbl"><b>Daily reminder</b><small className="wrap">{reminder.kind === 'on' ? 'Only if you haven’t played yet' : 'One notification a day'}</small></div>
            <div className="seg small-seg">
              <button type="button" className={reminder.kind === 'on' ? 'on' : ''} disabled={flip.busy} onClick={() => reminder.kind === 'off' && void flip.on()}>On</button>
              <button type="button" className={reminder.kind === 'off' ? 'on' : ''} disabled={flip.busy} onClick={() => reminder.kind === 'on' && void flip.off()}>Off</button>
            </div>
          </div>
        )}
        {reminder.kind === 'on' && (
          <label className="set">
            <div className="lbl"><b>Remind me at</b><small>Your local time</small></div>
            <select className="hour-select" value={reminder.hour} disabled={flip.busy} onChange={(e) => void flip.hour(Number(e.target.value))}>
              {HOURS.map((h) => <option key={h} value={h}>{formatHour(h)}</option>)}
            </select>
          </label>
        )}
        {reminder.kind === 'on' && <TestReminder />}
        {reminder.kind === 'blocked' && (
          <div className="set"><div className="lbl"><b>Daily reminder</b><small className="wrap">Notifications are blocked for this site. Allow them in your browser’s settings to turn it on.</small></div></div>
        )}
        {reminder.kind === 'needs-install' && (
          <div className="set"><div className="lbl"><b>Daily reminder</b><small className="wrap">Add the site to your Home Screen, then open it from there to turn this on.</small></div></div>
        )}
        {way === 'prompt' && (
          <div className="set"><div className="lbl"><b>Home screen</b><small>Open each edition like an app</small></div><button type="button" className="btn ghost sm" onClick={addToHomeScreen}>Add</button></div>
        )}
        {way === 'ios' && (
          <div className="set"><div className="lbl"><b>Home screen</b><small className="wrap">In Safari, tap Share, then Add to Home Screen</small></div></div>
        )}
      </div>
    </>
  )
}

/** How long Settings waits to hear that a test arrived. */
const ARRIVAL_MS = 30_000

type TestStep = 'idle' | 'sending' | 'waiting' | 'arrived' | 'late' | 'failed'

const TEST_NOTE: Record<Exclude<TestStep, 'failed'>, string> = {
  idle: 'Check that notifications reach this device',
  sending: 'Sending…',
  waiting: 'Sent. Waiting for it to arrive…',
  arrived: 'It arrived. If no notification showed up, notifications for this browser are off in your device’s settings.',
  late: 'Sent, but it hasn’t arrived. Your device may be holding it back to save battery: check this browser’s battery and notification settings.',
}

/** Settings: sends this browser a test notification and says whether it got here. */
function TestReminder() {
  const toast = useToast()
  const [step, setStep] = useState<TestStep>('idle')
  const [failure, setFailure] = useState('')
  const busy = step === 'sending' || step === 'waiting'

  function fail(note: string) {
    setFailure(note)
    setStep('failed')
  }

  async function send() {
    setStep('sending')
    let test: ReminderTest
    try {
      test = await sendTestReminder()
    } catch (err) {
      return fail(err instanceof ApiFailure && err.status === 429 ? 'That’s a lot of tests. Try again in an hour.' : 'Couldn’t send a test. Try again.')
    }
    // The reminder is off now, so this row is gone: a toast says why.
    if (test.outcome === 'ended') return toast('This browser’s notifications expired, so the reminder is off. Turn it on again.', 3600)
    if (test.outcome !== 'sent') return fail(`The push service couldn’t take it${test.status ? ` (${test.status})` : ''}. Try again in a few minutes.`)
    setStep('waiting')
    setStep((await arrival(test.id, ARRIVAL_MS)) ? 'arrived' : 'late')
  }

  return (
    <div className="set">
      <div className="lbl">
        <b>Test it</b>
        <small className="wrap" aria-live="polite">{step === 'failed' ? failure : TEST_NOTE[step]}</small>
      </div>
      <button type="button" className="btn ghost sm" disabled={busy} onClick={() => void send()}>{step === 'sending' ? 'Sending…' : step === 'waiting' ? 'Waiting…' : 'Send a test'}</button>
    </div>
  )
}
